import { OverlayHighlightDetails } from '@fullstory/page-insights-ui';
import { SimpleRect } from '@fullstory/util/rect';

export const INDICATOR_SIZE = 28; // This needs to match $indicatorWidth in shared.scss
const FORCE_FACTOR = 0.1; // Determines how far the indicators move on each iteration of the force algorithm.

interface Point {
  x: number;
  y: number;
}

export const calculateRankIndicatorPositions = (
  clickOverlayDetails: OverlayHighlightDetails[],
  inverseScale: number,
) => {
  let centroids: Point[] = [];
  let scaledSize: number = -1;

  for (const clickOverlayDetail of clickOverlayDetails) {
    if (scaledSize == -1) {
      // This assumes all indicators are the same size, which they are (currently).
      scaledSize = getScaledSize(inverseScale);
    }
    centroids.push(getInitialCentroid(clickOverlayDetail.clientRect, inverseScale));
  }

  if (centroids.length == 0) {
    // There were no indicators; return.
    return [];
  }

  // Use a force-directed graph algorithm to move intersecting points.
  // TODO(stephanie): Choose a more meaningful number of iterations.
  for (let i = 0; i < 10; i++) {
    centroids = moveCentroids(centroids, scaledSize);
  }

  const rankIndicatorPositions: SimpleRect[] = [];

  clickOverlayDetails.forEach((clientRect, i) => {
    // New bounding box.
    const left = centroids[i]!.x;
    const top = centroids[i]!.y;
    rankIndicatorPositions.push({
      left,
      top,
      right: left + INDICATOR_SIZE,
      bottom: top + INDICATOR_SIZE,
      width: INDICATOR_SIZE,
      height: INDICATOR_SIZE,
    });
  });

  return rankIndicatorPositions;
};

export const getScaledSize = (inverseScale: number): number => INDICATOR_SIZE * inverseScale;

export const getInitialCentroid = (elemRect: SimpleRect, inverseScale: number): Point => {
  const scaledSize = getScaledSize(inverseScale);
  return <Point>{
    x: elemRect.left - scaledSize / 2,
    y: elemRect.top - scaledSize / 2,
  };
};

// Points contains the current centroids for the markers, size is the width/height of each marker.
// This method users a basic force algorithm to calculate the repulsion between indicators and move them accordingly.
// If 2 indicators do not overlap, their repulsion is 0.
const moveCentroids = (points: Point[], size: number): Point[] => {
  const output: Point[] = [];

  // For each point, sum the forces acting on it and then move it a little bit.
  for (let i = 0; i < points.length; i++) {
    let xForce = 0;
    let yForce = 0;
    for (let j = 0; j < points.length; j++) {
      if (i == j) {
        continue;
      }

      const xDist = points[i]!.x - points[j]!.x;
      const yDist = points[i]!.y - points[j]!.y;
      // Only add a force if the 2 overlap.
      if (Math.abs(xDist) < size && Math.abs(yDist) < size) {
        xForce += xDist;
        yForce += yDist;
      }
    }
    output.push({
      x: points[i]!.x + FORCE_FACTOR * xForce,
      y: points[i]!.y + FORCE_FACTOR * yForce,
    } as Point);
  }
  return output;
};
